import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useMutation } from "convex/react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  ListChecks,
  Mic2,
  Music2,
  SlidersHorizontal,
} from "lucide-react"
import { useState } from "react"
import { api } from "../../../../convex/_generated/api"
import {
  ALL_DECADES,
  lcConfigSchema,
  type Decade,
} from "@/games/lyrically-challenged/schemas"
import { analyticsEvents } from "@/lib/analytics/events"
import { buildLyricallyChallengedEventNamePreview } from "@/lib/eventNames"
import { inNumberRange, parseIntegerDraft } from "@/lib/numberDraft"
import { trackEvent } from "@/lib/analytics/posthog"
import { VenueField, useVenueResolver } from "@/components/VenueField"

export const Route = createFileRoute("/_authed/host/events/new")({
  component: NewEventWizard,
})

type SubmitState = "idle" | "submitting" | "saved"
type StepId = "basics" | "game" | "review"

const steps: Array<{
  id: StepId
  label: string
  eyebrow: string
}> = [
  { id: "basics", label: "Date & venue", eyebrow: "event details" },
  { id: "game", label: "Game", eyebrow: "round assumptions" },
  { id: "review", label: "Review", eyebrow: "confirm setup" },
]

const maxTeamsRange = { min: 1, max: 100 }
const dealersChoiceRange = { min: 1, max: 20 }

function NewEventWizard() {
  const createWizardEvent = useMutation(api.events.createLyricallyChallengedEvent)
  const { resolve: resolveVenue } = useVenueResolver()
  const navigate = useNavigate()
  const reduce = useReducedMotion()

  const [stepIndex, setStepIndex] = useState(0)
  const [startsAt, setStartsAt] = useState(defaultStartsAt())
  const [venueName, setVenueName] = useState("")
  const [decades, setDecades] = useState<Set<Decade>>(
    () => new Set(ALL_DECADES),
  )
  const [maxTeamsDraft, setMaxTeamsDraft] = useState("30")
  const [dealersChoiceRoundDraft, setDealersChoiceRoundDraft] = useState("5")
  const [submitState, setSubmitState] = useState<SubmitState>("idle")
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState({
    startsAt: false,
    game: false,
  })

  const currentStep = steps[stepIndex]
  const selectedDecades = ALL_DECADES.filter((d) => decades.has(d))
  const startsAtMs = new Date(startsAt).getTime()
  const startsAtValid = Boolean(startsAt) && Number.isFinite(startsAtMs)
  const eventLabel = buildLyricallyChallengedEventNamePreview(
    venueName,
    startsAt,
  )
  const basicsComplete = startsAtValid
  const maxTeams = parseIntegerDraft(maxTeamsDraft)
  const dealersChoiceRound = parseIntegerDraft(dealersChoiceRoundDraft)
  const maxTeamsValid = inNumberRange(maxTeams, maxTeamsRange)
  const dealersChoiceValid = inNumberRange(dealersChoiceRound, dealersChoiceRange)
  const gameComplete =
    selectedDecades.length > 0 &&
    maxTeamsValid &&
    dealersChoiceValid
  const canSubmit = basicsComplete && gameComplete && submitState === "idle"

  function markBasicsTouched() {
    setTouched((prev) => ({ ...prev, startsAt: true }))
  }

  function goNext() {
    setError(null)
    if (currentStep.id === "basics" && !basicsComplete) {
      markBasicsTouched()
      return
    }
    if (currentStep.id === "game" && !gameComplete) {
      setTouched((prev) => ({ ...prev, game: true }))
      return
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1))
  }

  function goBack() {
    setError(null)
    setStepIndex((i) => Math.max(i - 1, 0))
  }

  function toggleDecade(decade: Decade) {
    setTouched((prev) => ({ ...prev, game: true }))
    setDecades((prev) => {
      const next = new Set(prev)
      if (next.has(decade)) next.delete(decade)
      else next.add(decade)
      return next
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    markBasicsTouched()
    setTouched((prev) => ({ ...prev, game: true }))

    if (
      !basicsComplete ||
      !gameComplete ||
      maxTeams === null ||
      dealersChoiceRound === null
    ) {
      setError("Finish the missing setup before creating the event.")
      return
    }

    setSubmitState("submitting")
    setError(null)
    try {
      const config = {
        decadesEnabled: selectedDecades,
        maxTeams,
        dealersChoiceRound,
      }
      const parsedConfig = lcConfigSchema.parse(config)
      const venueId = await resolveVenue(venueName)
      const result = await createWizardEvent({
        startsAt: startsAtMs,
        venueId,
        config,
      })

      trackEvent(analyticsEvents.hostEventCreated, {
        eventId: result.eventId,
        hasVenue: Boolean(venueId),
        creationFlow: "wizard",
        createsInitialGame: true,
      })
      trackEvent(analyticsEvents.lcGameCreated, {
        eventId: result.eventId,
        gameId: result.gameId,
        creationFlow: "event_wizard",
        decadesEnabledCount: parsedConfig.decadesEnabled.length,
        maxTeams: parsedConfig.maxTeams,
        dealersChoiceRound: parsedConfig.dealersChoiceRound,
      })

      setSubmitState("saved")
      setTimeout(
        () =>
          navigate({
            to: "/host/events/$eventId",
            params: { eventId: result.eventId },
          }),
        reduce ? 0 : 300,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSubmitState("idle")
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="mx-auto max-w-[1120px]">
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link
            to="/host/events"
            className="inline-flex items-center gap-1 rounded-full text-sm font-semibold text-[color:var(--muted-foreground)] transition-colors hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            All events
          </Link>
          <p className="console-kicker mt-4">Event setup</p>
          <h2 className="mt-1 text-3xl font-bold tracking-tight">
            New event
          </h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-[color:var(--muted-foreground)]">
            Create the scheduled event and first Lyrically Challenged game in one pass.
          </p>
        </div>

        <div className="console-panel rounded-2xl px-4 py-3 text-sm">
          <p className="font-semibold text-[color:var(--foreground)]">
            Creates event + first game
          </p>
          <p className="mt-1 text-[color:var(--muted-foreground)]">
            Nothing goes live until the host starts it.
          </p>
        </div>
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-[16.5rem_minmax(0,1fr)]">
        <aside className="space-y-4">
          <StepRail currentIndex={stepIndex} />
          <AssumptionPanel
            selectedDecades={selectedDecades}
            dealersChoiceRound={dealersChoiceRound}
          />
        </aside>

        <section className="console-panel rounded-[1.25rem] p-4 sm:p-5">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={currentStep.id}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <StepHeader step={currentStep} />

              {currentStep.id === "basics" && (
                <BasicsStep
                  startsAt={startsAt}
                  setStartsAt={setStartsAt}
                  venueName={venueName}
                  setVenueName={setVenueName}
                  startsAtInvalid={touched.startsAt && !startsAtValid}
                  markStartsAtTouched={() =>
                    setTouched((prev) => ({ ...prev, startsAt: true }))
                  }
                  eventLabel={eventLabel}
                />
              )}

              {currentStep.id === "game" && (
                <GameStep
                  selectedDecades={selectedDecades}
                  toggleDecade={toggleDecade}
                  decadeInvalid={touched.game && selectedDecades.length === 0}
                  maxTeams={maxTeamsDraft}
                  setMaxTeams={setMaxTeamsDraft}
                  maxTeamsValid={maxTeamsValid}
                  dealersChoiceRound={dealersChoiceRoundDraft}
                  setDealersChoiceRound={setDealersChoiceRoundDraft}
                  dealersChoiceValid={dealersChoiceValid}
                  showNumberErrors={touched.game}
                  touchGame={() =>
                    setTouched((prev) => ({ ...prev, game: true }))
                  }
                />
              )}

              {currentStep.id === "review" && (
                <ReviewStep
                  eventLabel={eventLabel}
                  startsAt={startsAt}
                  venueName={venueName}
                  selectedDecades={selectedDecades}
                  maxTeams={maxTeams}
                  dealersChoiceRound={dealersChoiceRound}
                />
              )}
            </motion.div>
          </AnimatePresence>

          <AnimatePresence>
            {error && (
              <motion.p
                key="err"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                role="alert"
                className="mt-5 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-900"
              >
                <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{error}</span>
              </motion.p>
            )}
          </AnimatePresence>

          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[color:var(--border)] pt-4">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold transition-colors duration-150 hover:bg-[color:var(--surface-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                Back
              </button>
            )}
            <div className="flex-1" />
            <Link
              to="/host/events"
              className="rounded-full border border-transparent px-4 py-2 text-sm font-semibold text-[color:var(--muted-foreground)] transition-colors duration-150 hover:border-[color:var(--border)] hover:bg-[color:var(--surface-soft)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            >
              Cancel
            </Link>
            {currentStep.id === "review" ? (
              <SubmitButton state={submitState} disabled={!canSubmit} />
            ) : (
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]"
              >
                Continue
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
        </section>
      </div>
    </form>
  )
}

function StepRail({ currentIndex }: { currentIndex: number }) {
  return (
    <div className="console-panel rounded-[1.25rem] p-2">
      <ol className="space-y-1">
        {steps.map((step, index) => {
          const complete = index < currentIndex
          const active = index === currentIndex
          return (
            <li key={step.id}>
              <div
                className={[
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors",
                  active
                    ? "bg-[color:var(--surface)] text-[color:var(--foreground)] shadow-[0_12px_28px_-24px_rgba(23,22,52,0.8)]"
                    : "text-[color:var(--muted-foreground)] hover:bg-[color:var(--surface-soft)]",
                ].join(" ")}
              >
                <span
                  className={[
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
                    complete
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-[color:var(--border)] bg-[color:var(--background)]",
                  ].join(" ")}
                >
                  {complete ? <Check className="h-3.5 w-3.5" aria-hidden /> : index + 1}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold">{step.label}</span>
                  <span className="block truncate text-[11px] font-semibold text-[color:var(--muted-foreground)]">
                    {step.eyebrow}
                  </span>
                </span>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function AssumptionPanel({
  selectedDecades,
  dealersChoiceRound,
}: {
  selectedDecades: Decade[]
  dealersChoiceRound: number | null
}) {
  const dealersChoiceLabel =
    dealersChoiceRound === null ? "Not set" : `Round ${dealersChoiceRound}`
  const rows = [
    ["Game", "Lyrically Challenged"],
    ["Create", "Event plus first game"],
    ["Status", "Scheduled"],
    ["Decades", selectedDecades.length ? selectedDecades.join(", ") : "None"],
    ["Magic #s", "Portal managed"],
    ["Dealer's choice", dealersChoiceLabel],
  ]
  return (
    <div className="console-panel rounded-[1.25rem] p-4">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-[color:var(--primary)]" aria-hidden />
        <h2 className="text-sm font-bold">Assumptions</h2>
      </div>
      <dl className="mt-3 divide-y divide-[color:var(--border)]">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-[5.7rem_minmax(0,1fr)] gap-3 py-2">
            <dt className="text-[11px] font-bold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
              {label}
            </dt>
            <dd className="text-sm font-semibold leading-5 text-[color:var(--foreground)]">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function StepHeader({
  step,
}: {
  step: (typeof steps)[number]
}) {
  const Icon =
    step.id === "basics"
      ? CalendarDays
      : step.id === "game"
        ? SlidersHorizontal
        : Check
  return (
    <div className="mb-5 flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--primary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.88)]">
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <div>
        <p className="console-kicker">
          {step.eyebrow}
        </p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight">{step.label}</h2>
      </div>
    </div>
  )
}

function BasicsStep({
  startsAt,
  setStartsAt,
  venueName,
  setVenueName,
  startsAtInvalid,
  markStartsAtTouched,
  eventLabel,
}: {
  startsAt: string
  setStartsAt: (value: string) => void
  venueName: string
  setVenueName: (value: string) => void
  startsAtInvalid: boolean
  markStartsAtTouched: () => void
  eventLabel: string
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Starts at"
          hint={startsAtInvalid ? "Pick a real date and time." : "Defaults to next Saturday at 7:00 PM."}
          invalid={startsAtInvalid}
        >
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            onBlur={markStartsAtTouched}
            required
            aria-invalid={startsAtInvalid || undefined}
            className={inputClass(startsAtInvalid)}
          />
        </Field>
        <VenueField value={venueName} onChange={setVenueName} />
      </div>

      <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.86)]">
        <p className="console-kicker">
          Event label
        </p>
        <p className="mt-2 text-lg font-bold tracking-tight text-[color:var(--foreground)]">
          {eventLabel}
        </p>
        <p className="mt-1 text-xs leading-5 text-[color:var(--muted-foreground)]">
          Generated from the game, venue, and date so hosts do not have to
          invent a title.
        </p>
      </div>
    </div>
  )
}

function GameStep({
  selectedDecades,
  toggleDecade,
  decadeInvalid,
  maxTeams,
  setMaxTeams,
  maxTeamsValid,
  dealersChoiceRound,
  setDealersChoiceRound,
  dealersChoiceValid,
  showNumberErrors,
  touchGame,
}: {
  selectedDecades: Decade[]
  toggleDecade: (decade: Decade) => void
  decadeInvalid: boolean
  maxTeams: string
  setMaxTeams: (value: string) => void
  maxTeamsValid: boolean
  dealersChoiceRound: string
  setDealersChoiceRound: (value: string) => void
  dealersChoiceValid: boolean
  showNumberErrors: boolean
  touchGame: () => void
}) {
  return (
    <div className="space-y-5">
      <section>
        <div className="mb-2 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-[color:var(--foreground)]">
              Decades enabled
            </p>
            <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
              These are the choices available to teams each round.
            </p>
          </div>
          <span className="rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] px-2.5 py-1 text-xs font-bold text-[color:var(--muted-foreground)]">
            {selectedDecades.length} selected
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {ALL_DECADES.map((decade) => {
            const selected = selectedDecades.includes(decade)
            return (
              <button
                key={decade}
                type="button"
                onClick={() => toggleDecade(decade)}
                aria-pressed={selected}
                className={[
                  "flex min-h-14 items-center justify-between rounded-xl border px-3 py-2 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 active:scale-[0.98]",
                  selected
                    ? "border-primary bg-primary text-primary-foreground shadow-[0_14px_34px_-26px_rgba(67,65,201,0.9)]"
                    : "border-[color:var(--border)] bg-[color:var(--surface)] hover:bg-[color:var(--surface-hover)]",
                ].join(" ")}
              >
                <span className="text-lg font-semibold">{decade}</span>
                {selected && <Check className="h-4 w-4" aria-hidden />}
              </button>
            )
          })}
        </div>
        {decadeInvalid && (
          <p className="mt-2 text-xs text-red-700">Pick at least one decade.</p>
        )}
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <NumberField
          label="Max teams"
          value={maxTeams}
          onChange={setMaxTeams}
          onBlur={touchGame}
          valid={maxTeamsValid}
          showError={showNumberErrors}
          range={maxTeamsRange}
          hint="Soft cap for team entries."
        />
        <NumberField
          label="Dealer's choice"
          value={dealersChoiceRound}
          onChange={setDealersChoiceRound}
          onBlur={touchGame}
          valid={dealersChoiceValid}
          showError={showNumberErrors}
          range={dealersChoiceRange}
          hint="Round where host assigns decades."
        />
      </section>

      <div className="grid gap-3 border-t border-[color:var(--border)] pt-4 sm:grid-cols-2">
        <AssumptionTile
          icon={<Music2 className="h-4 w-4" aria-hidden />}
          label="Player side"
          value="Teams pick one of the enabled decades and claim an available magic number."
        />
        <AssumptionTile
          icon={<Mic2 className="h-4 w-4" aria-hidden />}
          label="Host side"
          value="Scores are entered manually after each performance reveal."
        />
      </div>
    </div>
  )
}

function ReviewStep({
  eventLabel,
  startsAt,
  venueName,
  selectedDecades,
  maxTeams,
  dealersChoiceRound,
}: {
  eventLabel: string
  startsAt: string
  venueName: string
  selectedDecades: Decade[]
  maxTeams: number | null
  dealersChoiceRound: number | null
}) {
  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)] shadow-[inset_0_1px_0_rgba(255,255,255,0.86)]">
        <ReviewRow label="Event" value={eventLabel} />
        <ReviewRow label="When" value={formatStartsAt(startsAt)} />
        <ReviewRow label="Venue" value={venueName.trim() || "No venue yet"} />
        <ReviewRow label="Game" value="Lyrically Challenged" />
        <ReviewRow label="Decades" value={selectedDecades.join(", ")} />
        <ReviewRow label="Magic numbers" value="Managed in the portal" />
        <ReviewRow
          label="Max teams"
          value={maxTeams === null ? "Not set" : String(maxTeams)}
        />
        <ReviewRow
          label="Dealer's choice"
          value={
            dealersChoiceRound === null
              ? "Not set"
              : `Round ${dealersChoiceRound}`
          }
        />
      </div>

      <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.86)]">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-emerald-600" aria-hidden />
          <h3 className="text-sm font-bold">Ready to create</h3>
        </div>
        <p className="mt-2 text-sm leading-6 text-[color:var(--muted-foreground)]">
          This will create a scheduled event and open the Lyrically Challenged
          event setup screen next.
        </p>
      </div>
    </div>
  )
}

function SubmitButton({
  state,
  disabled,
}: {
  state: SubmitState
  disabled: boolean
}) {
  const reduce = useReducedMotion()
  const label =
    state === "submitting"
      ? "Creating..."
      : state === "saved"
        ? "Created"
        : "Create event and game"

  return (
    <button
      type="submit"
      disabled={disabled || state !== "idle"}
      aria-live="polite"
      className={[
        "relative inline-flex min-w-[12rem] items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold shadow-[0_14px_32px_-22px_rgba(67,65,201,0.9)] transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
        state === "saved"
          ? "bg-emerald-600 text-white"
          : "bg-primary text-primary-foreground hover:brightness-105 active:scale-[0.98]",
        "disabled:cursor-not-allowed disabled:opacity-50",
      ].join(" ")}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={state}
          initial={{ opacity: 0, y: reduce ? 0 : 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduce ? 0 : -4 }}
          transition={{ duration: 0.16, ease: "easeOut" }}
          className="inline-flex items-center gap-1.5"
        >
          {state === "saved" && <Check className="h-4 w-4" aria-hidden />}
          {state === "submitting" && <Spinner />}
          {label}
        </motion.span>
      </AnimatePresence>
    </button>
  )
}

function NumberField({
  label,
  value,
  onChange,
  onBlur,
  valid,
  showError,
  range,
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  valid: boolean
  showError: boolean
  range: { min: number; max: number }
  hint: string
}) {
  const invalid = showError && !valid
  return (
    <Field
      label={label}
      hint={invalid ? `${label} must be ${range.min}-${range.max}.` : hint}
      invalid={invalid}
    >
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={invalid || undefined}
        className={inputClass(invalid)}
      />
    </Field>
  )
}

function AssumptionTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]">
      <div className="flex items-center gap-2 text-[color:var(--primary)]">
        {icon}
        <p className="text-[11px] font-bold uppercase tracking-[0.08em]">
          {label}
        </p>
      </div>
      <p className="mt-2 text-sm leading-5 text-[color:var(--muted-foreground)]">
        {value}
      </p>
    </div>
  )
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-2 border-b border-[color:var(--border)] px-4 py-3 last:border-b-0 sm:grid-cols-[9rem_minmax(0,1fr)]">
      <dt className="text-[11px] font-bold uppercase tracking-[0.08em] text-[color:var(--muted-foreground)]">
        {label}
      </dt>
      <dd className="text-sm font-semibold text-[color:var(--foreground)]">
        {value}
      </dd>
    </div>
  )
}

function Field({
  label,
  children,
  hint,
  invalid,
}: {
  label: string
  children: React.ReactNode
  hint?: string
  invalid?: boolean
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-[color:var(--foreground)]">
        {label}
      </span>
      {children}
      <AnimatePresence>
        {hint && (
          <motion.span
            key={hint}
            initial={{ opacity: 0, y: -2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -2 }}
            transition={{ duration: 0.16 }}
            className={`mt-1 block text-xs ${invalid ? "text-red-700" : "text-[color:var(--muted-foreground)]"}`}
          >
            {hint}
          </motion.span>
        )}
      </AnimatePresence>
    </label>
  )
}

function Spinner() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      className="animate-spin"
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

function inputClass(invalid: boolean) {
  return [
    "w-full rounded-xl border bg-[color:var(--surface)] px-3.5 py-2.5 text-sm font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.86)] transition-colors duration-150 placeholder:text-[color:var(--muted-foreground)]/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
    invalid
      ? "border-red-300 focus-visible:ring-red-300/40"
      : "border-[color:var(--border)] focus-visible:border-primary/40",
  ].join(" ")
}

function formatStartsAt(value: string) {
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return "No start time"
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}

/** Default to next Saturday 7pm local - covers the typical trivia-night case. */
function defaultStartsAt(): string {
  const d = new Date()
  const daysUntilSat = (6 - d.getDay() + 7) % 7 || 7
  d.setDate(d.getDate() + daysUntilSat)
  d.setHours(19, 0, 0, 0)
  // toISOString gives Z; datetime-local wants "YYYY-MM-DDTHH:MM" in local time.
  const tzOffsetMs = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16)
}
